import { render, showApiError } from '../main';
import {
  API_ERR_LOAD_OVERVIEW, API_ERR_LOAD_MODERATION_LOG, API_ERR_LOAD_USERS,
  API_ERR_UPDATE_STATUS, API_ERR_LOAD_REPORTS, API_ERR_LOAD_BLOCKS, API_ERR_LOAD_STATS,
} from '../utils/app-strings';
import { adminApi } from '../api';
import { escapeHtml } from '../utils/string';
import { getRouteParams } from '../router';

// ── State ──

let currentTab = 'overview';
const state = {
  moderation: { page: 1, flagged: '', content_type: '', source: '' },
  users: { page: 1, user_type: '', search: '', sort: 'created_at', order: 'desc' },
  reports: { page: 1, status: '' },
  blocks: { page: 1 },
  stats: { days: 7 },
};

// ── Entry point ──

export async function renderAdmin(): Promise<void> {
  const validTabs = ['overview', 'moderation', 'users', 'reports', 'blocks', 'stats'];
  const params = getRouteParams();
  const initialTab = params.tab && validTabs.includes(params.tab) ? params.tab : 'overview';
  currentTab = initialTab;

  render(`
    <div class="view-frame admin-view">
      <div class="page-header">
        <h1 class="page-title">Admin</h1>
      </div>
      <div class="admin-tabs" id="adminTabs">
        ${validTabs.map(tab => `<button class="admin-tab ${tab === initialTab ? 'active' : ''}" data-tab="${tab}">${tab.charAt(0).toUpperCase() + tab.slice(1)}</button>`).join('')}
      </div>
      <div class="view-scroll" id="adminContent">
        <div class="loading"><div class="spinner"></div></div>
      </div>
    </div>
  `);

  document.getElementById('adminTabs')!.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('button');
    if (!btn) return;
    document.querySelectorAll('.admin-tab').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    currentTab = btn.dataset.tab!;
    history.replaceState(null, '', `#/admin/${currentTab}`);
    loadTab();
  });

  await loadTab();
}

// ── Tab loader ──

async function loadTab(): Promise<void> {
  const loaders: Record<string, () => Promise<void>> = {
    overview: loadOverview,
    moderation: loadModeration,
    users: loadUsers,
    reports: loadReports,
    blocks: loadBlocks,
    stats: loadStats,
  };
  await loaders[currentTab]?.();
}

// ── Helpers ──

function esc(s: string | null | undefined): string {
  return s ? escapeHtml(s) : '';
}

function typeBadge(t: number): string {
  if (t === 1) return '<span class="badge badge-info">AI</span>';
  if (t === 2) return '<span class="badge badge-muted">Test</span>';
  return '<span class="badge badge-primary">Human</span>';
}

function flagBadge(f: number): string {
  return f
    ? '<span class="badge badge-danger">Flagged</span>'
    : '<span class="badge badge-success">OK</span>';
}

const CONTENT_TYPE_LABELS: Record<string, string> = {
  chat_message: 'Chat Message',
  image: 'Image',
  profile_text: 'Profile Text',
  qa_text: 'QA Text',
  photo_caption: 'Photo Caption',
  handle: 'Handle',
};

function contentTypeLabel(type: string): string {
  return CONTENT_TYPE_LABELS[type] || type;
}

function fieldDisplay(row: any): string {
  if (row.field_name) return esc(row.field_name);
  return `<span class="text-dim">${contentTypeLabel(row.content_type)}</span>`;
}

function contentDisplay(row: any): string {
  if (row.thumb_url) {
    return `<img class="admin-thumb" src="${esc(row.thumb_url)}" />`;
  }
  if (row.content_preview) {
    return `<span class="admin-content-preview" title="${esc(row.content_preview)}">${esc(row.content_preview)}</span>`;
  }
  return '<span class="text-dim">-</span>';
}

function fmtDate(d: string | null): string {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateTime(d: string | null): string {
  if (!d) return '-';
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function paginationHtml(page: number, total: number, limit: number, id: string): string {
  const totalPages = Math.ceil(total / limit) || 1;
  return `<div class="admin-pagination">
    <button class="btn btn-outline btn-sm" data-page="${page - 1}" data-pager="${id}" ${page <= 1 ? 'disabled' : ''}>Prev</button>
    <span class="admin-page-info">Page ${page} of ${totalPages} (${total} total)</span>
    <button class="btn btn-outline btn-sm" data-page="${page + 1}" data-pager="${id}" ${page >= totalPages ? 'disabled' : ''}>Next</button>
  </div>`;
}

function bindPager(id: string, onPage: (p: number) => void): void {
  document.querySelectorAll(`[data-pager="${id}"]`).forEach(btn => {
    btn.addEventListener('click', () => {
      const p = parseInt((btn as HTMLElement).dataset.page!);
      if (!isNaN(p)) onPage(p);
    });
  });
}

function content(): HTMLElement {
  return document.getElementById('adminContent')!;
}

function showLoading(): void {
  content().innerHTML = '<div class="loading"><div class="spinner"></div></div>';
}

function showError(msg: string): void {
  content().innerHTML = `<div class="loading" style="color:var(--text-muted)">${esc(msg)}</div>`;
}

// ── Overview ──

async function loadOverview(): Promise<void> {
  showLoading();
  try {
    const { data: s } = await adminApi.overview();
    const cards: [string, number][] = [
      ['Human Users', s.human_users], ['AI Users', s.ai_users], ['Test Users', s.test_users],
      ['Active Today', s.active_today], ['Active This Week', s.active_this_week],
      ['Matches', s.total_matches], ['Chats', s.total_chats], ['Messages', s.total_messages],
      ['Pending Reports', s.pending_reports], ['Total Reports', s.total_reports],
      ['Blocks', s.total_blocks], ['Flagged Content', s.flagged_content], ['Rejected Images', s.rejected_images],
    ];
    content().innerHTML = `<div class="admin-stat-grid">
      ${cards.map(([label, val]) => `
        <div class="admin-stat-card">
          <div class="admin-stat-value">${(val ?? 0).toLocaleString()}</div>
          <div class="admin-stat-label">${label}</div>
        </div>
      `).join('')}
    </div>`;
  } catch (err) {
    showApiError(err, API_ERR_LOAD_OVERVIEW);
  }
}

// ── Moderation ──

async function loadModeration(): Promise<void> {
  showLoading();
  const s = state.moderation;
  try {
    const params: Record<string, any> = { page: s.page, limit: 50 };
    if (s.flagged) params.flagged = s.flagged;
    if (s.content_type) params.content_type = s.content_type;
    if (s.source) params.source = s.source;

    const { data } = await adminApi.moderation(params);

    content().innerHTML = `
      <div class="admin-filters">
        <select id="modFlagged">
          <option value="">All Results</option>
          <option value="1" ${s.flagged === '1' ? 'selected' : ''}>Flagged Only</option>
          <option value="0" ${s.flagged === '0' ? 'selected' : ''}>Clean Only</option>
        </select>
        <select id="modType">
          <option value="">All Types</option>
          <option value="image" ${s.content_type === 'image' ? 'selected' : ''}>Image</option>
          <option value="chat_message" ${s.content_type === 'chat_message' ? 'selected' : ''}>Chat</option>
          <option value="profile_text" ${s.content_type === 'profile_text' ? 'selected' : ''}>Profile Text</option>
          <option value="qa_text" ${s.content_type === 'qa_text' ? 'selected' : ''}>QA Text</option>
        </select>
        <select id="modSource">
          <option value="">All Sources</option>
          <option value="openai_moderation" ${s.source === 'openai_moderation' ? 'selected' : ''}>OpenAI Moderation</option>
          <option value="openai_gpt" ${s.source === 'openai_gpt' ? 'selected' : ''}>OpenAI GPT</option>
        </select>
      </div>
      <div class="admin-table-wrap">
        <table class="admin-table">
          <thead><tr><th>User</th><th>Type</th><th>Field</th><th>Content</th><th>Result</th><th>Category</th><th>Score</th><th>Source</th><th>Date</th></tr></thead>
          <tbody>
            ${data.rows.map((r: any) => `<tr>
              <td>${esc(r.first_name)}<br><small class="text-dim">${esc(r.email)}</small></td>
              <td>${contentTypeLabel(r.content_type)}</td>
              <td>${fieldDisplay(r)}</td>
              <td>${contentDisplay(r)}</td>
              <td>${flagBadge(r.flagged)}</td>
              <td>${esc(r.highest_category) || '-'}</td>
              <td>${r.highest_score != null ? Number(r.highest_score).toFixed(2) : '-'}</td>
              <td>${esc(r.source)}</td>
              <td>${fmtDateTime(r.created_at)}</td>
            </tr>`).join('')}
            ${data.rows.length === 0 ? '<tr><td colspan="9" class="text-dim" style="text-align:center">No results</td></tr>' : ''}
          </tbody>
        </table>
      </div>
      ${paginationHtml(s.page, data.total, 50, 'mod')}
    `;

    // Bind filters
    const bind = (id: string, key: keyof typeof s) => {
      document.getElementById(id)?.addEventListener('change', (e) => {
        (s as any)[key] = (e.target as HTMLSelectElement).value;
        s.page = 1;
        loadModeration();
      });
    };
    bind('modFlagged', 'flagged');
    bind('modType', 'content_type');
    bind('modSource', 'source');
    bindPager('mod', (p) => { s.page = p; loadModeration(); });
  } catch (err) {
    showApiError(err, API_ERR_LOAD_MODERATION_LOG);
  }
}

// ── Users ──

let userSearchTimer: ReturnType<typeof setTimeout>;

async function loadUsers(): Promise<void> {
  showLoading();
  const s = state.users;
  try {
    const params: Record<string, any> = { page: s.page, limit: 50, sort: s.sort, order: s.order };
    if (s.user_type) params.user_type = s.user_type;
    if (s.search) params.search = s.search;

    const { data } = await adminApi.users(params);

    content().innerHTML = `
      <div class="admin-filters">
        <input type="text" id="userSearch" class="input" placeholder="Search name or email..." value="${esc(s.search)}" />
        <select id="userType">
          <option value="">All Types</option>
          <option value="0" ${s.user_type === '0' ? 'selected' : ''}>Human</option>
          <option value="1" ${s.user_type === '1' ? 'selected' : ''}>AI</option>
          <option value="2" ${s.user_type === '2' ? 'selected' : ''}>Test</option>
        </select>
        <select id="userSort">
          <option value="created_at" ${s.sort === 'created_at' ? 'selected' : ''}>Joined</option>
          <option value="last_active_at" ${s.sort === 'last_active_at' ? 'selected' : ''}>Last Active</option>
          <option value="name" ${s.sort === 'name' ? 'selected' : ''}>Name</option>
          <option value="email" ${s.sort === 'email' ? 'selected' : ''}>Email</option>
        </select>
        <select id="userOrder">
          <option value="desc" ${s.order === 'desc' ? 'selected' : ''}>Newest</option>
          <option value="asc" ${s.order === 'asc' ? 'selected' : ''}>Oldest</option>
        </select>
      </div>
      <div class="admin-table-wrap">
        <table class="admin-table">
          <thead><tr><th></th><th>Name</th><th>Email</th><th>Type</th><th>Active</th><th>City</th><th>Age</th><th>Joined</th><th>Last Active</th></tr></thead>
          <tbody>
            ${data.rows.map((r: any) => {
              const age = r.birth_date ? Math.floor((Date.now() - new Date(r.birth_date).getTime()) / 31557600000) : '-';
              return `<tr>
                <td>${r.avatar_url ? `<img class="admin-avatar" src="${esc(r.avatar_url)}" />` : '<div class="admin-avatar"></div>'}</td>
                <td>${esc(r.first_name) || '-'}</td>
                <td>${esc(r.email) || '-'}</td>
                <td>${typeBadge(r.user_type)}</td>
                <td>${r.is_active ? 'Yes' : 'No'}</td>
                <td>${esc(r.city) || '-'}</td>
                <td>${age}</td>
                <td>${fmtDate(r.created_at)}</td>
                <td>${fmtDateTime(r.last_active_at)}</td>
              </tr>`;
            }).join('')}
            ${data.rows.length === 0 ? '<tr><td colspan="9" class="text-dim" style="text-align:center">No results</td></tr>' : ''}
          </tbody>
        </table>
      </div>
      ${paginationHtml(s.page, data.total, 50, 'users')}
    `;

    document.getElementById('userSearch')?.addEventListener('input', (e) => {
      clearTimeout(userSearchTimer);
      userSearchTimer = setTimeout(() => {
        s.search = (e.target as HTMLInputElement).value;
        s.page = 1;
        loadUsers();
      }, 400);
    });
    const bindSelect = (id: string, key: keyof typeof s) => {
      document.getElementById(id)?.addEventListener('change', (e) => {
        (s as any)[key] = (e.target as HTMLSelectElement).value;
        s.page = 1;
        loadUsers();
      });
    };
    bindSelect('userType', 'user_type');
    bindSelect('userSort', 'sort');
    bindSelect('userOrder', 'order');
    bindPager('users', (p) => { s.page = p; loadUsers(); });
  } catch (err) {
    showApiError(err, API_ERR_LOAD_USERS);
  }
}

// ── Reports ──

async function loadReports(): Promise<void> {
  showLoading();
  const s = state.reports;
  try {
    const params: Record<string, any> = { page: s.page, limit: 50 };
    if (s.status) params.status = s.status;

    const { data } = await adminApi.reports(params);

    content().innerHTML = `
      <div class="admin-filters">
        <select id="reportStatus">
          <option value="">All Statuses</option>
          <option value="pending" ${s.status === 'pending' ? 'selected' : ''}>Pending</option>
          <option value="reviewed" ${s.status === 'reviewed' ? 'selected' : ''}>Reviewed</option>
          <option value="actioned" ${s.status === 'actioned' ? 'selected' : ''}>Actioned</option>
          <option value="dismissed" ${s.status === 'dismissed' ? 'selected' : ''}>Dismissed</option>
        </select>
      </div>
      <div class="admin-table-wrap">
        <table class="admin-table">
          <thead><tr><th>Reporter</th><th>Target</th><th>Category</th><th>Description</th><th>Status</th><th>Date</th></tr></thead>
          <tbody>
            ${data.rows.map((r: any) => `<tr>
              <td>${esc(r.reporter_name)} ${typeBadge(r.reporter_type)}<br><small class="text-dim">${esc(r.reporter_email)}</small></td>
              <td>${esc(r.target_name)} ${typeBadge(r.target_type)}<br><small class="text-dim">${esc(r.target_email)}</small></td>
              <td>${esc(r.category)}</td>
              <td class="truncate" style="max-width:200px" title="${esc(r.description)}">${esc(r.description) || '-'}</td>
              <td>
                <select class="admin-status-select" data-report-id="${r.id}">
                  ${['pending', 'reviewed', 'actioned', 'dismissed'].map(st =>
                    `<option value="${st}" ${r.status === st ? 'selected' : ''}>${st}</option>`
                  ).join('')}
                </select>
              </td>
              <td>${fmtDate(r.created_at)}</td>
            </tr>`).join('')}
            ${data.rows.length === 0 ? '<tr><td colspan="6" class="text-dim" style="text-align:center">No results</td></tr>' : ''}
          </tbody>
        </table>
      </div>
      ${paginationHtml(s.page, data.total, 50, 'reports')}
    `;

    document.getElementById('reportStatus')?.addEventListener('change', (e) => {
      s.status = (e.target as HTMLSelectElement).value;
      s.page = 1;
      loadReports();
    });

    document.querySelectorAll('.admin-status-select').forEach(sel => {
      sel.addEventListener('change', async (e) => {
        const select = e.target as HTMLSelectElement;
        const id = select.dataset.reportId!;
        try {
          await adminApi.updateReportStatus(id, select.value);
        } catch (err) {
          showApiError(err, API_ERR_UPDATE_STATUS);
        }
      });
    });

    bindPager('reports', (p) => { s.page = p; loadReports(); });
  } catch (err) {
    showApiError(err, API_ERR_LOAD_REPORTS);
  }
}

// ── Blocks ──

async function loadBlocks(): Promise<void> {
  showLoading();
  const s = state.blocks;
  try {
    const { data } = await adminApi.blocks({ page: s.page, limit: 50 });

    content().innerHTML = `
      <div class="admin-table-wrap">
        <table class="admin-table">
          <thead><tr><th>Blocker</th><th>Blocked</th><th>Reason</th><th>Date</th></tr></thead>
          <tbody>
            ${data.rows.map((r: any) => `<tr>
              <td>${esc(r.blocker_name)}<br><small class="text-dim">${esc(r.blocker_email)}</small></td>
              <td>${esc(r.blocked_name)}<br><small class="text-dim">${esc(r.blocked_email)}</small></td>
              <td>${esc(r.reason) || '-'}</td>
              <td>${fmtDate(r.created_at)}</td>
            </tr>`).join('')}
            ${data.rows.length === 0 ? '<tr><td colspan="4" class="text-dim" style="text-align:center">No results</td></tr>' : ''}
          </tbody>
        </table>
      </div>
      ${paginationHtml(s.page, data.total, 50, 'blocks')}
    `;

    bindPager('blocks', (p) => { s.page = p; loadBlocks(); });
  } catch (err) {
    showApiError(err, API_ERR_LOAD_BLOCKS);
  }
}

// ── Stats ──

async function loadStats(): Promise<void> {
  showLoading();
  const days = state.stats.days;
  try {
    const { data } = await adminApi.stats(days);

    content().innerHTML = `
      <div class="admin-period-bar">
        ${[7, 14, 30, 90].map(d =>
          `<button class="btn btn-sm ${days === d ? 'btn-primary' : 'btn-outline'}" data-days="${d}">${d}d</button>`
        ).join('')}
      </div>

      <div class="admin-stats-section">
        <h3 class="admin-section-title">Signups (Human)</h3>
        <table class="admin-table"><thead><tr><th>Date</th><th>Count</th></tr></thead><tbody>
          ${data.signups.map((r: any) => `<tr><td>${r.date}</td><td>${r.count}</td></tr>`).join('')}
          ${data.signups.length === 0 ? '<tr><td colspan="2" class="text-dim">No data</td></tr>' : ''}
        </tbody></table>
      </div>

      <div class="admin-stats-section">
        <h3 class="admin-section-title">Messages</h3>
        <table class="admin-table"><thead><tr><th>Date</th><th>Count</th></tr></thead><tbody>
          ${data.messages.map((r: any) => `<tr><td>${r.date}</td><td>${r.count}</td></tr>`).join('')}
          ${data.messages.length === 0 ? '<tr><td colspan="2" class="text-dim">No data</td></tr>' : ''}
        </tbody></table>
      </div>

      <div class="admin-stats-section">
        <h3 class="admin-section-title">Chats</h3>
        <table class="admin-table"><thead><tr><th>Date</th><th>Count</th></tr></thead><tbody>
          ${data.chats.map((r: any) => `<tr><td>${r.date}</td><td>${r.count}</td></tr>`).join('')}
          ${data.chats.length === 0 ? '<tr><td colspan="2" class="text-dim">No data</td></tr>' : ''}
        </tbody></table>
      </div>

      <div class="admin-stats-section">
        <h3 class="admin-section-title">Interactions</h3>
        <table class="admin-table"><thead><tr><th>Date</th><th>Total</th><th>Likes</th><th>Hides</th></tr></thead><tbody>
          ${data.interactions.map((r: any) => `<tr><td>${r.date}</td><td>${r.total}</td><td>${r.likes}</td><td>${r.hides}</td></tr>`).join('')}
          ${data.interactions.length === 0 ? '<tr><td colspan="4" class="text-dim">No data</td></tr>' : ''}
        </tbody></table>
      </div>
    `;

    document.querySelectorAll('.admin-period-bar button').forEach(btn => {
      btn.addEventListener('click', () => {
        state.stats.days = parseInt((btn as HTMLElement).dataset.days!);
        loadStats();
      });
    });
  } catch (err) {
    showApiError(err, API_ERR_LOAD_STATS);
  }
}
